import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router';
import { Check, Clock, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAccountStatus } from '@/hooks/queries/useAccountStatus';
import { useSubscription } from '@/hooks/queries/useSubscription';
import { derivePhase, graceProgressPercent } from '@/lib/billing/phase';
import { formatCurrency } from '@/lib/currency';
import { cn } from '@/lib/utils';

const SESSION_GATE_PREFIX = 'velko.grace.shown';
const HARD_GRACE_DAYS = 14;

function todayKey(): string {
  return new Date().toISOString().slice(0, 10);
}

export function GracePeriodSheet() {
  const { t } = useTranslation('billing');
  const { data: status } = useAccountStatus();
  const { data: subscription } = useSubscription();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  const phase = useMemo(() => {
    if (!status) return null;
    return derivePhase(
      status.access_state,
      status.days_to_expiry,
      status.grace_days_left,
      status.trial_days_left,
      status.write_restricted,
    );
  }, [status]);

  useEffect(() => {
    if (!status || !phase) return;
    if (phase.phase !== 'grace') return;
    // Suppress on subscription pages
    if (location.pathname.startsWith('/subscription')) return;

    const key = `${SESSION_GATE_PREFIX}.${todayKey()}`;
    try {
      if (sessionStorage.getItem(key) === '1') return;
      sessionStorage.setItem(key, '1');
    } catch {
      // sessionStorage unavailable — open anyway
    }
    setOpen(true);
  }, [status, phase, location.pathname]);

  if (!open || !status || !phase || phase.phase !== 'grace') return null;

  const isHardGrace = status.access_state === 'grace_hard';
  const tone = isHardGrace ? 'critical' : 'soft';
  // Prefer the authoritative server value; fall back to reconstruction only
  // when it's absent (older API responses).
  const daysSinceExpiry =
    status.days_since_expiry && status.days_since_expiry > 0
      ? status.days_since_expiry
      : HARD_GRACE_DAYS - Math.max(0, status.grace_days_left);
  const progress = graceProgressPercent(status.grace_days_left, HARD_GRACE_DAYS);

  const sub = subscription?.subscription ?? null;
  const planName = sub?.plan?.display_name ?? sub?.plan?.name ?? '—';
  const price = sub?.plan?.price_monthly ?? 0;

  const headBg = tone === 'critical' ? 'bg-error' : 'bg-warning';
  const headText = tone === 'critical' ? 'text-white' : 'text-charcoal-950';
  const headEyebrowOpacity = tone === 'critical' ? 'opacity-90' : 'opacity-80';
  const numAccent = tone === 'critical' ? 'text-error' : 'text-warning-dark';
  const progressFill =
    tone === 'critical' ? 'bg-gradient-to-r from-warning to-error' : 'bg-warning';

  const ctaPrice = price > 0 ? `${formatCurrency(price, 'XOF')}` : '';

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="grace-period-title"
      className="fixed inset-0 z-[60] flex items-center justify-center bg-charcoal-950/70 backdrop-blur-sm p-4"
    >
      <div className="w-full max-w-[560px] overflow-hidden rounded-2xl border-[2.5px] border-charcoal-950 bg-white shadow-[6px_6px_0_#0d0d1a]">
        <div
          className={cn(
            'relative px-7 py-6 border-b-[2.5px] border-charcoal-950',
            headBg,
            headText,
          )}
        >
          <p
            className={cn(
              'font-mono text-[11px] font-bold uppercase tracking-[0.08em] mb-1',
              headEyebrowOpacity,
            )}
          >
            {t('grace_sheet_eyebrow')}
          </p>
          <h3
            id="grace-period-title"
            className="font-heading text-xl sm:text-2xl font-extrabold leading-tight"
          >
            {t('grace_sheet_title', { days: daysSinceExpiry })}
          </h3>
          <button
            onClick={() => setOpen(false)}
            aria-label={t('grace_sheet_close')}
            className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-md border-2 border-charcoal-950 bg-charcoal-950 text-white"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>

        <div className="p-6 sm:p-7">
          <div className="text-center mb-5">
            <div className="font-mono font-bold text-[56px] sm:text-[72px] leading-none tracking-tight text-charcoal-950">
              <span className={numAccent}>{status.grace_days_left}</span>{' '}
              <span className="text-charcoal-950">{t('days_label')}</span>
            </div>
            <p className="mt-1.5 font-heading text-xs font-bold uppercase tracking-[0.1em] text-charcoal-600">
              {tone === 'critical'
                ? t('grace_sheet_subtitle_critical')
                : t('grace_sheet_subtitle_soft')}
            </p>
          </div>

          <div className="mb-1 h-3.5 overflow-hidden rounded-[10px] border-2 border-charcoal-950 bg-charcoal-100">
            <span
              className={cn('block h-full', progressFill)}
              style={{ width: `${progress}%` }}
              aria-hidden
            />
          </div>
          <div className="mb-5 flex justify-between font-mono text-[11px] font-bold uppercase tracking-[0.06em] text-charcoal-500">
            <span>
              {t('grace_sheet_day_x_of_y', { current: daysSinceExpiry, total: HARD_GRACE_DAYS })}
            </span>
            <span>
              {sub?.ends_at
                ? t('grace_sheet_suspension_at', {
                    date: new Date(sub.ends_at).toLocaleDateString('fr-FR', {
                      day: 'numeric',
                      month: 'short',
                    }),
                  })
                : null}
            </span>
          </div>

          {sub && (
            <div className="mb-5 flex items-center justify-between rounded-md border-2 border-charcoal-950 bg-charcoal-50 px-4 py-3">
              <div className="flex items-center gap-2.5">
                <span className="rounded font-mono text-[10px] font-bold uppercase tracking-wider border-[1.5px] border-charcoal-950 bg-lime-500 px-2 py-0.5 text-charcoal-950">
                  {t('grace_sheet_your_plan')}
                </span>
                <b className="font-heading font-bold text-[15px]">{planName}</b>
              </div>
              {price > 0 && (
                <div className="font-heading text-[15px] font-extrabold">
                  {formatCurrency(price, 'XOF')}
                  <small className="ml-0.5 font-sans text-xs font-medium text-charcoal-500">
                    {t('per_month')}
                  </small>
                </div>
              )}
            </div>
          )}

          <ul className="mb-5 grid grid-cols-1 gap-2 sm:grid-cols-3">
            {[
              t('grace_sheet_trust_data_safe'),
              t('grace_sheet_trust_mobile_money'),
              t('grace_sheet_trust_cancel_anytime'),
            ].map((item) => (
              <li
                key={item}
                className="flex items-center gap-2 text-xs font-medium text-charcoal-700"
              >
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded border-[1.5px] border-charcoal-950 bg-lime-100">
                  <Check className="h-3 w-3 text-charcoal-950" />
                </span>
                {item}
              </li>
            ))}
          </ul>

          <div className="flex flex-col gap-2.5">
            <Link
              to="/subscription/plans"
              className="inline-flex items-center justify-center gap-2 rounded-md border-[2.5px] border-charcoal-950 bg-lime-500 px-5 py-3.5 font-heading text-sm font-bold text-charcoal-950 shadow-[3px_3px_0_#0d0d1a] transition-transform hover:-translate-x-px hover:-translate-y-px hover:shadow-[5px_5px_0_#0d0d1a]"
              onClick={() => setOpen(false)}
            >
              {t('grace_sheet_cta_renew_with_price', {
                price: ctaPrice,
                defaultValue: ctaPrice
                  ? `Renouveler maintenant — ${ctaPrice}`
                  : 'Renouveler maintenant',
              })}
            </Link>
            <Link
              to="/subscription/plans"
              className="inline-flex items-center justify-center rounded-md border-[2.5px] border-charcoal-950 bg-white px-5 py-3 font-heading text-sm font-bold text-charcoal-950 hover:bg-charcoal-50"
              onClick={() => setOpen(false)}
            >
              {t('grace_sheet_cta_view_plans')}
            </Link>
            <button
              onClick={() => setOpen(false)}
              className="self-center mt-1 px-2 py-2 text-xs font-semibold text-charcoal-500 underline underline-offset-[3px] hover:text-charcoal-950"
            >
              <Clock className="mr-1 inline-block h-3 w-3" />
              {t('grace_sheet_cta_remind_me')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
